'use client'
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useSeatmapStore from '../../../../../../../../stores/seatmapStore'
import { useTooltips } from '../../providers/TooltipProvider'
import {
  countSectionAvailability,
  sectionAvailabilityGradientId,
  sectionOverlayPaint,
} from './sectionAvailability'

/** Matches the gray field on the venue background so hidden blocks disappear into it. */
const HIDDEN_SECTION_FILL = '#D6D8DB'

/** Same-fill seam blend: Q+WCQ, J+WCJ only (venue-tuned; not QQ/JJ). */
function isSectionStrokeSeamBlendTarget(section) {
  const raw = String(section?.sectionNumber ?? '')
    .trim()
    .toUpperCase()
  if (raw === 'Q' || raw === 'J') return true
  const compact = raw.replace(/\s+/g, '')
  return compact === 'WCQ' || compact === 'WCJ'
}

/** WC companion polygons: no cover overlay / no letter art (`WC` + one letter). */
function isWheelchairCompanionSection(section) {
  const compact = String(section?.sectionNumber ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
  return /^WC[A-Z]$/.test(compact)
}

/** Slight inset so Q/WCQ and J/WCJ match neighboring sideline blocks. */
const Q_WCQ_GROUP_SCALE = 0.968

/**
 * Map data ids for the small WC shards beside Q/J (extra shrink).
 * WCQ: c3f2373e-b201-43f6-afb9-2380e64c9def · WCJ: c5b86bd0-43f8-41e9-b027-5be59134c735
 */
const WHEELCHAIR_SHARD_EXTRA_IDS = new Set([
  'c3f2373e-b201-43f6-afb9-2380e64c9def',
  'c5b86bd0-43f8-41e9-b027-5be59134c735',
])
/** Extra shrink on top of `Q_WCQ_GROUP_SCALE` for those ids only. */
const WHEELCHAIR_SHARD_EXTRA_SCALE = 0.78

function seamPairBundleScaleForSection(section) {
  const sid = String(section?.sectionId ?? '')
  let k = Q_WCQ_GROUP_SCALE
  if (WHEELCHAIR_SHARD_EXTRA_IDS.has(sid)) {
    k *= WHEELCHAIR_SHARD_EXTRA_SCALE
  }
  return k
}

/** White/light strokes between same‑fill polygons read as gaps. Match stroke to fill. */
function polygonStrokeForSeamBlend(stroke, fill) {
  if (stroke == null || stroke === '' || stroke === 'none') return stroke
  const f = fill != null && fill !== '' ? fill : '#3E8BF7'
  const raw = String(stroke).trim().toLowerCase()
  if (raw === 'white' || raw === '#fff' || raw === '#ffffff') return f
  const m = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (m) {
    const r = Number(m[1])
    const g = Number(m[2])
    const b = Number(m[3])
    if (r >= 245 && g >= 245 && b >= 245) return f
  }
  if (raw.startsWith('#')) {
    const hex =
      raw.length === 4
        ? `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`
        : raw
    if (hex === '#ffffff') return f
  }
  return stroke
}

/** Mexican-Heritage (seatmap id 17): L-shaped sideline blocks clip centered labels. */
const SECTION_LABEL_OVERRIDES = {
  17: {
    101: { yFactor: 0.62, fontScale: 0.88 },
    103: { yFactor: 0.38, fontScale: 0.88 },
  },
}

function getSectionLabelOverride(seatmapId, sectionNumber) {
  const map = SECTION_LABEL_OVERRIDES[Number(seatmapId)]
  if (!map) return null
  return map[String(sectionNumber ?? '').trim()] ?? null
}

/**
 * Section numbers come from SVG ids, and Illustrator/CorelDRAW encode characters
 * that are illegal in XML names, so `PARTY_x0020_DECKS_1` has to be decoded back
 * to `PARTY DECKS 1` before it can be shown to a fan.
 */
function sectionLabelText(sectionNumber) {
  return String(sectionNumber ?? '')
    .replace(/_x([0-9a-fA-F]{4})_/g, (_match, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const CHAR_WIDTH_RATIO = 0.72
const LINE_HEIGHT_RATIO = 0.9
const MAX_LABEL_LINES = 3
const FILL_PROBE_SAMPLES = 33
const LABEL_ROW_FACTORS = [0.5, 0.44, 0.56, 0.37, 0.63]
const SOLID_ROW_RATIO = 0.92

function svgProbePoint(pathEl, x, y) {
  const svg = pathEl.ownerSVGElement
  if (svg?.createSVGPoint) {
    const point = svg.createSVGPoint()
    point.x = x
    point.y = y
    return point
  }
  return { x, y }
}

/** Longest uninterrupted stretch of fill along one axis, in user units. */
function longestFilledRun(pathEl, from, to, fixed, axis, preferValue) {
  const step = (to - from) / (FILL_PROBE_SAMPLES - 1)
  if (!Number.isFinite(step) || step <= 0) return null

  const runs = []
  let start = -1
  for (let index = 0; index <= FILL_PROBE_SAMPLES; index += 1) {
    const value = from + step * index
    const inside =
      index < FILL_PROBE_SAMPLES &&
      pathEl.isPointInFill(
        axis === 'x'
          ? svgProbePoint(pathEl, value, fixed)
          : svgProbePoint(pathEl, fixed, value)
      )
    if (inside) {
      if (start < 0) start = index
      continue
    }
    if (start >= 0) {
      const runFrom = from + step * start
      const runTo = from + step * (index - 1)
      runs.push({ from: runFrom, to: runTo, size: runTo - runFrom })
      start = -1
    }
  }
  if (!runs.length) return null

  const preferred =
    preferValue == null
      ? null
      : runs.find((run) => run.from <= preferValue && preferValue <= run.to)
  const run =
    preferred ??
    runs.reduce((widest, candidate) =>
      candidate.size > widest.size ? candidate : widest
    )
  return { ...run, center: (run.from + run.to) / 2 }
}

/**
 * Bounding boxes of curved or L-shaped sections are far wider than the band of
 * colour a label can sit in, so a box-centred label spills onto the background.
 * Probe the fill for the widest row the text actually fits inside.
 */
function measureLabelBox(pathEl, rect, overrideYFactor) {
  if (typeof pathEl?.isPointInFill !== 'function') return null

  const rowFactors =
    overrideYFactor == null ? LABEL_ROW_FACTORS : [overrideYFactor]
  let best = null
  for (const factor of rowFactors) {
    const y = rect.y + rect.height * factor
    const row = longestFilledRun(
      pathEl,
      rect.x,
      rect.x + rect.width,
      y,
      'x',
      null
    )
    if (!row || row.size <= 0) continue
    if (!best || row.size > best.row.size) best = { row, y }
    // A row spanning the whole box means the section is a plain block there, so
    // the remaining probes can only confirm what we already have.
    if (row.size >= rect.width * SOLID_ROW_RATIO) break
  }
  if (!best) return null
  if (best.row.size >= rect.width * SOLID_ROW_RATIO) {
    return {
      x: best.row.from,
      y: best.y,
      width: best.row.size,
      height: rect.height,
    }
  }

  const column = longestFilledRun(
    pathEl,
    rect.y,
    rect.y + rect.height,
    best.row.center,
    'y',
    best.y
  )
  return {
    x: best.row.from,
    y: best.y,
    width: best.row.size,
    height: column && column.size > 0 ? column.size : rect.height,
  }
}

/** Split into `lineCount` lines of roughly equal length, on word boundaries. */
function wrapWords(words, lineCount) {
  if (lineCount <= 1) return [words.join(' ')]

  const target = words.join(' ').length / lineCount
  const lines = []
  let current = ''
  words.forEach((word) => {
    if (!current) {
      current = word
      return
    }
    const candidate = `${current} ${word}`
    const linesLeft = lineCount - lines.length - 1
    if (candidate.length > target && linesLeft > 0) {
      lines.push(current)
      current = word
      return
    }
    current = candidate
  })
  if (current) lines.push(current)
  return lines
}

/** Pick the line break-up that lets the label render at the largest size. */
function fitLabelLines(upper, box, maxFont, heightBudget) {
  const words = upper.split(' ').filter(Boolean)
  const maxLines = Math.min(MAX_LABEL_LINES, words.length)
  let best = null

  for (let lineCount = 1; lineCount <= maxLines; lineCount += 1) {
    const lines = wrapWords(words, lineCount)
    const longestLine = lines.reduce(
      (max, line) => Math.max(max, line.length),
      1
    )
    const byWidth = box.width / (longestLine * CHAR_WIDTH_RATIO)
    const blockLines = 1 + (lines.length - 1) * LINE_HEIGHT_RATIO
    const byHeight = heightBudget / blockLines
    const fontSize = Math.min(maxFont, byWidth, byHeight)
    if (!best || fontSize > best.fontSize) best = { lines, fontSize }
  }

  return best
}

function computeLabelLayout(rect, label, seatmapId, sectionNumber, pathEl) {
  if (!rect || rect.width <= 0 || rect.height <= 0) return null
  const rawText = sectionLabelText(label)
  if (!rawText) return null

  const upper = rawText.toUpperCase()
  const isNumeric = /^\d+$/.test(upper)
  const isClub = upper.startsWith('CLUB')
  const override = getSectionLabelOverride(seatmapId, sectionNumber)
  const fontScale = override?.fontScale ?? 1
  const box = measureLabelBox(pathEl, rect, override?.yFactor) ?? {
    x: rect.x,
    y: rect.y + rect.height * (override?.yFactor ?? 0.5),
    width: rect.width,
    height: rect.height,
  }

  const heightBudget = box.height * (isNumeric ? 0.36 : isClub ? 0.24 : 0.3)
  const maxFont = isNumeric ? 80 : isClub ? 34 : 52
  const fit = fitLabelLines(upper, box, maxFont, heightBudget)
  if (!fit) return null

  return {
    text: fit.lines.join('\n'),
    x: box.x + box.width / 2,
    y: box.y,
    fontSize: Math.max(12, fit.fontSize) * fontScale,
  }
}

function SectionNumberOverlaySvg({
  section,
  pathRefMap,
  show,
  pathD,
  seatmapId,
  fill = 'white',
}) {
  const [layout, setLayout] = useState(null)

  useLayoutEffect(() => {
    if (!show) {
      setLayout(null)
      return
    }

    let frame = 0
    let cancelled = false
    const measure = (attempt = 0) => {
      if (cancelled) return
      const pathEl = pathRefMap.current[String(section.sectionId)]
      if (!pathEl) {
        if (attempt < 4) {
          frame = requestAnimationFrame(() => measure(attempt + 1))
        }
        return
      }
      const rect = pathEl.getBBox?.()
      const next = computeLabelLayout(
        rect,
        section?.sectionNumber,
        seatmapId,
        section?.sectionNumber,
        pathEl
      )
      setLayout((prev) => {
        if (
          prev &&
          next &&
          prev.text === next.text &&
          prev.x === next.x &&
          prev.y === next.y &&
          prev.fontSize === next.fontSize
        ) {
          return prev
        }
        return next
      })
    }

    frame = requestAnimationFrame(() => measure(0))
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [
    pathRefMap,
    pathD,
    seatmapId,
    section?.sectionId,
    section?.sectionNumber,
    show,
  ])

  if (!show || !layout) return null
  const lines = layout.text.split('\n')
  const firstLineDy =
    -((lines.length - 1) * layout.fontSize * LINE_HEIGHT_RATIO) / 2
  return (
    <text
      x={layout.x}
      y={layout.y}
      fill={fill}
      fontSize={layout.fontSize}
      fontWeight="500"
      fontFamily="Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif"
      textAnchor="middle"
      dominantBaseline="middle"
      pointerEvents="none"
    >
      {lines.map((line, index) => (
        <tspan
          key={`${line}-${index}`}
          x={layout.x}
          dy={index === 0 ? firstLineDy : layout.fontSize * LINE_HEIGHT_RATIO}
        >
          {line}
        </tspan>
      ))}
    </text>
  )
}

function zoomableCoverPathD(section) {
  return (
    section?.coverPath ||
    section?.overlay?.path ||
    section?.svg?.coverPath ||
    section?.path
  )
}

function zoomableSectionLabelState(
  section,
  {
    showLabels,
    showCovers,
    uncoveredZoomableSectionIdSet,
    sectionIdsWithSeatLayer,
    soldOut = false,
  }
) {
  const sid = String(section.sectionId)
  const neighborUncovered = uncoveredZoomableSectionIdSet.has(sid)
  const inSeatView = showCovers && !showLabels
  const hasSeatDotsInView = Boolean(sectionIdsWithSeatLayer?.has(sid))
  // A sold-out cover stays up. Uncovering it draws every physical seat and
  // reads as a full section even when nothing is for sale.
  const treatUncovered =
    !soldOut && (neighborUncovered || (inSeatView && hasSeatDotsInView))
  const overlayVisible = !treatUncovered && (showLabels || showCovers)
  const hasIdentifierPath = Boolean(section?.identifier?.path)
  const coverD = zoomableCoverPathD(section)
  const baseD = section?.path || coverD
  const pathD = isWheelchairCompanionSection(section)
    ? baseD
    : overlayVisible
      ? coverD
      : baseD
  const showTextLabel =
    overlayVisible &&
    !isWheelchairCompanionSection(section) &&
    !hasIdentifierPath
  return {
    overlayVisible,
    showTextLabel,
    pathD,
    hasIdentifierPath,
    treatUncovered,
  }
}

const Sections = React.memo(
  ({
    data,
    sectionCoversEnabled = false,
    showLabels = true,
    showCovers = false,
    uncoveredZoomableSectionIds = [],
    sectionIdsWithSeatLayer = null,
    onZoomableSectionBounds,
    onSectionClick,
  }) => {
    const sectionLookupTable = useSeatmapStore(
      (state) => state.sectionLookupTable
    )
    const seatLookupTable = useSeatmapStore((state) => state.seatLookupTable)
    const seatmapId = useSeatmapStore((state) => state.seatmapId)
    const pathRefMap = useRef({})
    const pathRefCallbacks = useRef({})

    const registerPathRef = useCallback((sectionId) => {
      const key = String(sectionId)
      if (!pathRefCallbacks.current[key]) {
        pathRefCallbacks.current[key] = (el) => {
          if (el) pathRefMap.current[key] = el
          else delete pathRefMap.current[key]
        }
      }
      return pathRefCallbacks.current[key]
    }, [])

    const uncoveredZoomableSectionIdSet = useMemo(
      () => new Set(uncoveredZoomableSectionIds.map((id) => String(id))),
      [uncoveredZoomableSectionIds]
    )

    const sectionsList = useMemo(
      () => (data?.sections ? Object.values(data.sections) : []),
      [data?.sections]
    )

    const availabilityBySectionId = useMemo(() => {
      const map = new Map()
      for (const section of sectionsList) {
        if (!section?.zoomable) continue
        map.set(
          String(section.sectionId),
          countSectionAvailability(section, data?.rows, seatLookupTable)
        )
      }
      return map
    }, [data?.rows, seatLookupTable, sectionsList])

    const textLabelSections = useMemo(() => {
      const labelStateArgs = {
        showLabels,
        showCovers,
        uncoveredZoomableSectionIdSet,
        sectionIdsWithSeatLayer,
      }
      return sectionsList
        .map((section) => {
          if (section?.hidden) return null
          if (section?.zoomable) {
            if (!sectionCoversEnabled) return null
            const inventoryPaint = sectionOverlayPaint(
              section,
              availabilityBySectionId.get(String(section.sectionId))
            )
            const { showTextLabel, pathD } = zoomableSectionLabelState(
              section,
              { ...labelStateArgs, soldOut: inventoryPaint.soldOut }
            )
            if (!showTextLabel) return null
            return {
              section,
              show: true,
              pathD,
              labelFill: inventoryPaint.labelFill,
            }
          }
          if (
            isWheelchairCompanionSection(section) ||
            section?.identifier?.path
          ) {
            return null
          }
          return { section, show: true, pathD: section.path }
        })
        .filter(Boolean)
    }, [
      availabilityBySectionId,
      sectionCoversEnabled,
      sectionIdsWithSeatLayer,
      sectionsList,
      showCovers,
      showLabels,
      uncoveredZoomableSectionIdSet,
    ])

    return (
      <g className="polygons">
        {sectionsList.map((section) => {
          if (section?.hidden) {
            const strokeWidth =
              Math.max(Number(section.strokeWidth) || 0, 2) + 2
            return (
              <path
                key={section.sectionId}
                d={
                  section?.zoomable ? zoomableCoverPathD(section) : section.path
                }
                id={section.sectionId}
                data-section-hidden="true"
                fill={HIDDEN_SECTION_FILL}
                stroke={HIDDEN_SECTION_FILL}
                strokeWidth={strokeWidth}
                strokeLinejoin="miter"
                pointerEvents="none"
              />
            )
          }
          if (section?.zoomable) {
            if (!sectionCoversEnabled) return null
            const inventoryPaint = sectionOverlayPaint(
              section,
              availabilityBySectionId.get(String(section.sectionId))
            )
            return (
              <ZoomableSectionSvg
                key={section.sectionId}
                section={section}
                inventoryPaint={inventoryPaint}
                showLabels={showLabels}
                showCovers={showCovers}
                uncoveredZoomableSectionIdSet={uncoveredZoomableSectionIdSet}
                sectionIdsWithSeatLayer={sectionIdsWithSeatLayer}
                onZoomableSectionBounds={onZoomableSectionBounds}
                onSectionClick={onSectionClick}
                registerPathRef={registerPathRef}
              />
            )
          }
          return (
            <GASection
              key={section.sectionId}
              section={section}
              sectionLookupTable={sectionLookupTable}
              showLabels={showLabels}
              showCovers={showCovers}
              registerPathRef={registerPathRef}
            />
          )
        })}
        <g className="section-number-labels" pointerEvents="none">
          {textLabelSections.map(({ section, show, pathD, labelFill }) => (
            <SectionNumberOverlaySvg
              key={`label-${section.sectionId}`}
              section={section}
              pathRefMap={pathRefMap}
              show={show}
              pathD={pathD}
              seatmapId={seatmapId}
              fill={labelFill}
            />
          ))}
        </g>
      </g>
    )
  }
)

function zoomableCoverPaint(section, isPainted, inventoryPaint) {
  if (!isPainted) {
    return {
      fill: section?.uncoveredFill ?? '#FFFFFF',
      stroke: 'rgba(0,0,0,0)',
      strokeWidth: 0,
    }
  }
  if (inventoryPaint?.soldOut) {
    return {
      fill: inventoryPaint.fill,
      stroke: '#C5C8D0',
      strokeWidth: 2,
    }
  }
  const availableFill =
    inventoryPaint?.fill || section?.coverFill || section?.fill || '#3E8BF7'
  const fill =
    inventoryPaint?.kind === 'gradient'
      ? `url(#${sectionAvailabilityGradientId(section.sectionId)})`
      : availableFill
  const stroke =
    section?.coverStroke ?? section?.stroke ?? 'rgba(255, 255, 255, 0.95)'
  const strokeWidth = Number(
    section?.coverStrokeWidth ?? section?.strokeWidth ?? 2
  )
  const blendedStroke = polygonStrokeForSeamBlend(stroke, availableFill)
  const baseStrokeWidth = Number.isFinite(strokeWidth) ? strokeWidth : 2
  return {
    fill,
    stroke: isSectionStrokeSeamBlendTarget(section) ? blendedStroke : stroke,
    strokeWidth: isSectionStrokeSeamBlendTarget(section) ? 0 : baseStrokeWidth,
  }
}

function ZoomableSectionSvg({
  section,
  inventoryPaint,
  showLabels,
  showCovers,
  uncoveredZoomableSectionIdSet,
  sectionIdsWithSeatLayer = null,
  onZoomableSectionBounds,
  onSectionClick,
  registerPathRef,
}) {
  const pathRef = useRef(null)
  const bundleRef = useRef(null)
  const [bundleCenterScale, setBundleCenterScale] = useState(null)

  const pathElementRef = useMemo(() => {
    const register = registerPathRef(section.sectionId)
    return (el) => {
      pathRef.current = el
      register(el)
    }
  }, [registerPathRef, section.sectionId])

  const soldOut = Boolean(inventoryPaint?.soldOut)
  const { overlayVisible, pathD, hasIdentifierPath, treatUncovered } =
    zoomableSectionLabelState(section, {
      showLabels,
      showCovers,
      uncoveredZoomableSectionIdSet,
      sectionIdsWithSeatLayer,
      soldOut,
    })

  // Keep polygons clickable in seat view (switch zoom target); seats render above and keep hits.
  // Sold-out sections stay a gray cover and do not zoom into an empty seat grid.
  const interactive = !soldOut && (showLabels || showCovers)
  const { fill, stroke, strokeWidth } = zoomableCoverPaint(
    section,
    overlayVisible,
    inventoryPaint
  )
  const gradientId = sectionAvailabilityGradientId(section.sectionId)
  const availableRatio =
    inventoryPaint?.ratio != null ? String(inventoryPaint.ratio) : undefined

  const seamPair = isSectionStrokeSeamBlendTarget(section)

  useLayoutEffect(() => {
    const pathEl = pathRef.current
    if (!pathEl) {
      setBundleCenterScale(null)
      return
    }
    if (!seamPair) {
      setBundleCenterScale(null)
      return
    }
    const k = seamPairBundleScaleForSection(section)
    const b = pathEl.getBBox()
    const cx = b.x + b.width / 2
    const cy = b.y + b.height / 2
    setBundleCenterScale((prev) =>
      prev && prev.cx === cx && prev.cy === cy && prev.k === k
        ? prev
        : { cx, cy, k }
    )
  }, [seamPair, pathD, section.sectionId, section.sectionNumber])

  useLayoutEffect(() => {
    const boundsEl = bundleRef.current ?? pathRef.current
    if (!boundsEl) return
    if (seamPair && bundleCenterScale == null) return
    const bb = boundsEl.getBBox()
    onZoomableSectionBounds?.(section.sectionId, {
      x: bb.x,
      y: bb.y,
      width: bb.width,
      height: bb.height,
    })
  }, [
    bundleCenterScale,
    seamPair,
    pathD,
    section.sectionId,
    showLabels,
    showCovers,
    treatUncovered,
    onZoomableSectionBounds,
  ])

  const handleClick = (e) => {
    const el = bundleRef.current ?? pathRef.current
    if (!el) return
    const b = el.getBBox()
    onSectionClick?.(e, section, {
      x: b.x,
      y: b.y,
      width: b.width,
      height: b.height,
    })
  }

  /**
   * Label/map mode: pointerdown on sections was bubbling to the SVG pan handler,
   * so tiny movement turned clicks into pans and blocked zoom-into-section.
   * Seat mode: leave bubbling so pan still starts from section polygons.
   */
  const handlePointerDownCapture = showLabels
    ? (e) => {
        e.stopPropagation()
      }
    : undefined

  const idFill = soldOut
    ? (inventoryPaint?.labelFill ?? '#353945')
    : (section?.identifier?.fill ?? 'white')
  const idOpacity =
    section?.identifier?.opacity != null && section?.identifier?.opacity !== ''
      ? section.identifier.opacity
      : undefined

  const bundleTransform =
    seamPair && bundleCenterScale
      ? `translate(${bundleCenterScale.cx},${bundleCenterScale.cy}) scale(${bundleCenterScale.k}) translate(${-bundleCenterScale.cx},${-bundleCenterScale.cy})`
      : undefined

  return (
    <g
      data-zoomable-section="true"
      data-section-root={interactive ? 'true' : undefined}
      data-sold-out={soldOut ? 'true' : undefined}
      data-available-ratio={availableRatio}
      style={{ cursor: interactive ? 'pointer' : 'default' }}
    >
      <g
        ref={bundleRef}
        data-zoomable-overlay-bundle="true"
        transform={bundleTransform}
      >
        {overlayVisible && inventoryPaint?.kind === 'gradient' ? (
          <defs>
            <linearGradient
              id={gradientId}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0%" stopColor={inventoryPaint.unavailableFill} />
              <stop
                offset={`${(1 - inventoryPaint.ratio) * 100}%`}
                stopColor={inventoryPaint.unavailableFill}
              />
              <stop
                offset={`${(1 - inventoryPaint.ratio) * 100}%`}
                stopColor={inventoryPaint.fill}
              />
              <stop offset="100%" stopColor={inventoryPaint.fill} />
            </linearGradient>
          </defs>
        ) : null}
        <path
          ref={pathElementRef}
          d={pathD}
          id={section.sectionId}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="miter"
          pointerEvents={interactive ? 'auto' : 'none'}
          onPointerDownCapture={
            interactive ? handlePointerDownCapture : undefined
          }
          onClick={interactive ? handleClick : undefined}
        />
        {overlayVisible &&
        hasIdentifierPath &&
        !isWheelchairCompanionSection(section) ? (
          <path
            d={section.identifier.path}
            fill={idFill}
            opacity={idOpacity}
            fillRule={section?.identifier?.evenodd ? 'evenodd' : 'nonzero'}
            clipRule={section?.identifier?.evenodd ? 'evenodd' : 'nonzero'}
            style={{ pointerEvents: 'none' }}
          />
        ) : null}
      </g>
    </g>
  )
}

function GASection({
  section,
  sectionLookupTable,
  showLabels,
  showCovers = false,
  hideIdentifierArt = false,
  registerPathRef,
}) {
  const { showTooltip } = useTooltips()
  const hasIdentifierPath = Boolean(section?.identifier?.path)

  const handleSectionSelect = (e, sectionArg) => {
    showTooltip(sectionArg.sectionId, e.clientX, e.clientY)
  }

  const sectionSoldout = (sectionId) => {
    return !sectionLookupTable[sectionId]
  }

  const UNAVAILABLE_COLOR = '#E6E8EC'
  const sectionFill = sectionSoldout(section.sectionId)
    ? UNAVAILABLE_COLOR
    : section.fill

  const listening =
    Boolean(sectionLookupTable[section.sectionId]) &&
    (Boolean(showLabels) || Boolean(showCovers))

  const stopSvgPanForTooltip = listening
    ? (e) => {
        e.stopPropagation()
      }
    : undefined

  const isSeamBlendTarget = isSectionStrokeSeamBlendTarget(section)
  const pathStroke = isSeamBlendTarget
    ? polygonStrokeForSeamBlend(section?.stroke, sectionFill)
    : section?.stroke

  return (
    <g
      data-section-root="true"
      style={{ cursor: listening ? 'pointer' : 'default' }}
    >
      <path
        ref={registerPathRef?.(section.sectionId)}
        fill={sectionFill}
        d={section.path}
        stroke={pathStroke}
        strokeWidth={isSeamBlendTarget ? 0 : Number(section?.strokeWidth) || 0}
        id={section.sectionId}
        className="path"
        pointerEvents={listening ? 'auto' : 'none'}
        onPointerDownCapture={stopSvgPanForTooltip}
        onClick={listening ? (e) => handleSectionSelect(e, section) : undefined}
      />
      {!hideIdentifierArt &&
      hasIdentifierPath &&
      !isWheelchairCompanionSection(section) ? (
        <path
          d={section.identifier.path}
          fill={section?.identifier?.fill ?? 'white'}
          opacity={
            section?.identifier?.opacity != null &&
            section?.identifier?.opacity !== ''
              ? section.identifier.opacity
              : undefined
          }
          fillRule={section?.identifier?.evenodd ? 'evenodd' : 'nonzero'}
          clipRule={section?.identifier?.evenodd ? 'evenodd' : 'nonzero'}
          pointerEvents="none"
        />
      ) : null}
    </g>
  )
}

export { Sections }
